import React, { useMemo, useState } from "react";
import { Box, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SettingsEthernetOutlinedIcon from "@mui/icons-material/SettingsEthernetOutlined";

import DashboardSummaryStrip from "../../../../../components/Dashboard/DashboardSummaryStrip";
import Invoices from "../../Invoices";

const FinanceSection = ({
  busyById,
  formatCurrency,
  formatDate,
  invoices = [],
  loading,
  onApprove,
  onMoveToProcessing,
  resolveContractorName,
}) => {
  const [activeTab, setActiveTab] = useState("open");

  const statusRows = useMemo(() => {
    const byStatus = (status) => invoices.filter((row) => String(row.status || "").toLowerCase() === status);
    const totalAmount = (rows) => rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const currency = (rows) => rows.find((row) => row.currency)?.currency || "usd";
    return {
      all: invoices,
      open: byStatus("open"),
      approved: byStatus("approved"),
      processing: byStatus("processing"),
      paid: byStatus("paid"),
      totalAmount,
      currency,
    };
  }, [invoices]);

  const summaryItems = useMemo(
    () => [
      {
        label: "Open",
        value: statusRows.open.length,
        helper: formatCurrency(statusRows.totalAmount(statusRows.open), statusRows.currency(statusRows.open)),
      },
      {
        label: "Approved",
        value: statusRows.approved.length,
        helper: formatCurrency(statusRows.totalAmount(statusRows.approved), statusRows.currency(statusRows.approved)),
      },
      {
        label: "Processing",
        value: statusRows.processing.length,
        helper: formatCurrency(statusRows.totalAmount(statusRows.processing), statusRows.currency(statusRows.processing)),
      },
      {
        label: "Paid",
        value: statusRows.paid.length,
        helper: formatCurrency(statusRows.totalAmount(statusRows.paid), statusRows.currency(statusRows.paid)),
      },
    ],
    [formatCurrency, statusRows]
  );

  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Typography variant="h5" sx={{ fontWeight: 850 }}>
          Finance
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review contractor invoices, approve payment readiness, and track processing status.
        </Typography>
      </Stack>

      <DashboardSummaryStrip items={summaryItems} />

      <Paper variant="outlined" sx={{ borderColor: "#dadce0", borderRadius: 2, overflow: "hidden" }}>
        <Tabs
          value={activeTab}
          onChange={(_event, nextTab) => setActiveTab(nextTab)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 56,
            borderBottom: "1px solid #edf0f3",
            "& .MuiTab-root": {
              minHeight: 56,
              fontWeight: 800,
              textTransform: "none",
            },
          }}
        >
          <Tab
            value="open"
            icon={<ReceiptLongOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label="Open"
          />
          <Tab
            value="approved"
            icon={<CheckCircleOutlineOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label="Approved"
          />
          <Tab
            value="processing"
            icon={<SettingsEthernetOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label="Processing"
          />
          <Tab
            value="paid"
            icon={<PaidOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label="Paid"
          />
          <Tab
            value="all"
            icon={<ReceiptLongOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label="All"
          />
        </Tabs>

        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          <Invoices
            loading={loading}
            invoices={invoices}
            busyById={busyById}
            onApprove={onApprove}
            onMoveToProcessing={onMoveToProcessing}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            resolveContractorName={resolveContractorName}
            statusFilter={activeTab}
            showStatusFilter={false}
          />
        </Box>
      </Paper>
    </Stack>
  );
};

export default FinanceSection;
